import { useCallback, useRef, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import {
    Box,
    FormControl,
    FormControlLabel,
    FormLabel,
    Modal,
    Radio,
    RadioGroup,
} from '@mui/material';
import { AddPartnerApplication } from '@prisma/client';
import { Formik, FormikProps } from 'formik';
import {
    AddPartnerInput,
    Address,
    CompanyLookupResponse,
    CompanyReportResponse,
} from 'types/graphql';

import { CompanyLookUpList } from 'src/components/CompanyLookUpList';
import { CustomCurrencyInput } from 'src/components/CustomCurrencyInput';
import { CustomInput } from 'src/components/CustomInput';
import { DropDown } from 'src/components/FormikComponents';
import Loader from 'src/components/Loader/Loader';
import { AddressListModal } from 'src/components/Modals';
import { SearchInput } from 'src/components/SearchInput';
import { showErrorToast } from 'src/components/Toasts/toast';
import { BUSINESS_TYPES } from 'src/constants';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { useAddPartnerContext } from 'src/contexts/AddPartnerProvider';
import { COMPANY_LOOKUP } from 'src/graphql/queries/companyLookup';
import { useAddressLookUp, useValidateVAT } from 'src/hooks';
import { getCountryName } from 'src/lib/countryConfig';
import getPostalLabel from 'src/lib/postalLabel';
import { isApplicationEditable } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';
import { extractAddress } from 'src/utils/utils';

import { Card } from '../components';
import ContactInformationComponent from '../components/ContactInformationComponent';
import { NavigationButtons } from '../components/NavigationButtons';
import Step2AddressInformation from '../components/Step2AddressInformation';
import Step2PrimaryContactInformation from '../components/Step2PrimaryContactInformation';
import step2ValidationSchema from '../validation/addPartnerStep2';

export type DirectorType = {
    firstName: string;
    lastName: string;
    dateOfBirth?: string;
    middleNames?: string;
    position?: string;
    personalAddress?: {
        addressLine1?: string;
        addressLine2?: string;
        country?: string;
    };
    numberOfShares: number;
};

export type OpenModalState = {
    open: boolean;
    formValue?: string;
};

export const Step2 = () => {
    const [formattedAddressInformation, setFormattedAddressInformation] = useState<string | null>(
        null
    );
    const [formattedTradingAddress, setFormattedTradingAddress] = useState<string | null>(null);
    const [formattedRegisteredAddress, setFormattedRegisteredAddress] = useState<string | null>(
        null
    );
    const [formattedBillingAddress, setFormattedBillingAddress] = useState<string | null>(null);
    const [formattedServiceAddress, setFormattedServiceAddress] = useState<string | null>(null);

    const grid1Ref = useRef(null);
    const grid2Ref = useRef(null);
    const grid3Ref = useRef(null);
    const grid4Ref = useRef(null);
    const grid5Ref = useRef(null);
    const grid6Ref = useRef(null);
    const grid7Ref = useRef(null);
    const { application, accountManagerOptions, addPartner, handleBack, activeStep } =
        useAddPartnerContext();
    const [companies, setCompanies] = useState<CompanyLookupResponse[]>([]);
    // Modal state management
    const [openAddressModal, setOpenAddressModal] = useState<OpenModalState>({
        open: false,
    });
    const [isCompaniesOpen, setIsCompaniesOpen] = useState(false);
    const [vatRegistered, setIsVatRegistered] = useState(application?.pi_vatRegistered);

    const [businessSelectedType, setBusinessSelectedType] = useState<string>();
    const isSoleTrader = businessSelectedType === 'SOLE_TRADER';

    const { onValidateVAT, validatingVatNumber } = useValidateVAT();

    const [initialPhoneNumbers, setInitialPhoneNumbers] = useState({
        bci_: { mobile: '', telephone: '' },
        sci_: { mobile: '', telephone: '' },
    });
    const { findAddress, addresses, searchingForAddress } = useAddressLookUp();

    const [companyLookUp] = useLazyQuery(COMPANY_LOOKUP, {
        onError: (error) => {
            showErrorToast(error.message);
        },
        onCompleted: ({ companyLookUp }) => {
            setCompanies(companyLookUp);
        },
    });

    const DEFAULT_COUNTRY = getCountryName(application?.countryDetails?.alpha2Code);
    const postalLabel = getPostalLabel(application?.countryDetails?.alpha2Code);

    const onUseForContact = useCallback(
        (checked: boolean, namePrefix: string, formik: FormikProps<any>) => {
            const {
                pci_email,
                pci_firstName,
                pci_lastName,
                pci_mobilePhoneNumber,
                pci_telephoneNumber,
                pci_position,
                pci_ra_eircode,
                pci_ra_addressLine1,
                pci_ra_addressLine2,
                pci_ra_city,
                pci_ra_country,
            } = formik.values;
            const { bci_ra_county, sci_ra_county, ...rest } = formik.values;

            if (checked) {
                // Initialise the phone numbers for BCI & SCI
                setInitialPhoneNumbers((prev) => {
                    return {
                        ...prev,
                        [namePrefix]: {
                            mobile: formik.values.pci_mobilePhoneNumber,
                            telephone: formik.values.pci_telephoneNumber,
                        },
                    };
                });

                formik.setValues({
                    ...rest,
                    [`${namePrefix}email`]: pci_email,
                    [`${namePrefix}firstName`]: pci_firstName,
                    [`${namePrefix}lastName`]: pci_lastName,
                    [`${namePrefix}ra_addressLine1`]: pci_ra_addressLine1,
                    [`${namePrefix}mobilePhoneNumber`]: pci_mobilePhoneNumber,
                    [`${namePrefix}telephoneNumber`]: pci_telephoneNumber,
                    [`${namePrefix}position`]: pci_position,
                    [`${namePrefix}ra_addressLine2`]: pci_ra_addressLine2,
                    [`${namePrefix}ra_city`]: pci_ra_city,
                    [`${namePrefix}ra_country`]: pci_ra_country,
                    [`${namePrefix}ra_eircode`]: pci_ra_eircode,
                });
            } else {
                setInitialPhoneNumbers((prev) => {
                    return {
                        ...prev,
                        [namePrefix]: {
                            mobile: '',
                            telephone: '',
                        },
                    };
                });

                formik.setValues({
                    ...rest,
                    [`${namePrefix}email`]: '',
                    [`${namePrefix}firstName`]: '',
                    [`${namePrefix}lastName`]: '',
                    [`${namePrefix}ra_addressLine1`]: '',
                    [`${namePrefix}mobilePhoneNumber`]: '',
                    [`${namePrefix}telephoneNumber`]: '',
                    [`${namePrefix}position`]: '',
                    [`${namePrefix}ra_addressLine2`]: '',
                    [`${namePrefix}ra_city`]: '',
                    [`${namePrefix}ra_country`]: DEFAULT_COUNTRY,
                    [`${namePrefix}ra_eircode`]: '',
                });
            }
        },
        []
    );

    const initialValues: Partial<AddPartnerApplication> = {
        pi_BusinessType: application?.pi_BusinessType || '',
        pi_legalCompanyName: application?.pi_legalCompanyName || '',
        pi_website: application?.pi_website ?? '',
        pi_registeredNumber: application?.pi_registeredNumber || '',
        companyIncorporationDate: application?.companyIncorporationDate || null,
        pi_tradingName: application?.pi_tradingName || '',
        pi_vatNumber: application?.pi_vatNumber || '',
        pi_vatRegistered: !!application?.pi_vatRegistered || false,
        pi_accountManager: application?.pi_accountManager || null,
        pi_creditLimit: application?.pi_creditLimit || 200,
        pi_paymentTerms: application?.pi_paymentTerms || '12',
        ai_ra_eircode: application?.ai_ra_eircode || '',
        ai_ra_addressLine1: application?.ai_ra_addressLine1 || '',
        ai_ra_addressLine2: application?.ai_ra_addressLine2 || '',
        ai_ra_city: application?.ai_ra_city || '',
        ai_ra_county: application?.ai_ra_county || '',
        ai_ra_country: DEFAULT_COUNTRY,
        ai_ta_eircode: application?.ai_ta_eircode || '',
        ai_ta_addressLine1: application?.ai_ta_addressLine1 || '',
        ai_ta_addressLine2: application?.ai_ta_addressLine2 || '',
        ai_ta_city: application?.ai_ta_city || '',
        ai_ta_county: application?.ai_ta_county || '',
        ai_ta_country: DEFAULT_COUNTRY,
        pci_firstName: application?.pci_firstName || '',
        pci_lastName: application?.pci_lastName || '',
        pci_email: application?.pci_email || '',
        pci_mobilePhoneNumber: application?.pci_mobilePhoneNumber || '',
        pci_telephoneNumber: application?.pci_telephoneNumber || '',
        pci_position: application?.pci_position || '',
        pci_ra_eircode: application?.pci_ra_eircode || '',
        pci_ra_addressLine1: application?.pci_ra_addressLine1 || '',
        pci_ra_addressLine2: application?.pci_ra_addressLine2 || '',
        pci_ra_city: application?.pci_ra_city || '',
        pci_ra_county: application?.pci_ra_county || '',
        pci_ra_country: DEFAULT_COUNTRY,
        bci_firstName: application?.bci_firstName || '',
        bci_lastName: application?.bci_lastName || '',
        bci_email: application?.bci_email || '',
        bci_mobilePhoneNumber: application?.bci_mobilePhoneNumber || '',
        bci_telephoneNumber: application?.bci_telephoneNumber || '',
        bci_position: application?.bci_position || '',
        bci_ra_eircode: application?.bci_ra_eircode || '',
        bci_ra_addressLine1: application?.bci_ra_addressLine1 || '',
        bci_ra_addressLine2: application?.bci_ra_addressLine2 || '',
        bci_ra_city: application?.bci_ra_city || '',
        bci_ra_country: DEFAULT_COUNTRY,
        sci_firstName: application?.sci_firstName || '',
        sci_lastName: application?.sci_lastName || '',
        sci_email: application?.sci_email || '',
        sci_mobilePhoneNumber: application?.sci_mobilePhoneNumber || '',
        sci_telephoneNumber: application?.sci_telephoneNumber || '',
        sci_position: application?.sci_position || '',
        sci_ra_eircode: application?.sci_ra_eircode || '',
        sci_ra_addressLine1: application?.sci_ra_addressLine1 || '',
        sci_ra_addressLine2: application?.sci_ra_addressLine2 || '',
        sci_ra_city: application?.sci_ra_city || '',
        sci_ra_country: DEFAULT_COUNTRY,
        sci_ra_countryCode: application?.sci_ra_countryCode || '',
        directors: '',
        currency: application?.currency || 'EUR',
    };

    // Submit handler for the search button
    const onFindAddress = async (value, formValue) => {
        await findAddress(value, () => {
            return setOpenAddressModal({ open: true, formValue });
        });
    };

    const onRetrievalOfAddress = (selectedAddress: Address, formattedAddress: string) => {
        return (formik) => {
            formik.setFieldValue(
                `${openAddressModal.formValue}_eircode`,
                selectedAddress?.postalCode
            );

            formik.setFieldValue(
                `${openAddressModal.formValue}_addressLine1`,
                selectedAddress?.line1
            );

            formik.setFieldValue(
                `${openAddressModal.formValue}_addressLine2`,
                selectedAddress?.line2
            );

            formik.setFieldValue(`${openAddressModal.formValue}_city`, selectedAddress?.city);

            formik.setFieldValue(
                `${openAddressModal.formValue}_country`,
                selectedAddress?.countryName
            );

            if (
                openAddressModal?.formValue !== 'bci_ra' &&
                openAddressModal?.formValue !== 'sci_ra'
            ) {
                formik.setFieldValue(
                    `${openAddressModal.formValue}_county`,
                    selectedAddress?.province
                );
            }

            switch (openAddressModal?.formValue) {
                case 'ai_ra':
                    setFormattedAddressInformation(formattedAddress);
                    break;
                case 'ai_ta':
                    setFormattedTradingAddress(formattedAddress);
                    break;
                case 'pci_ra':
                    setFormattedRegisteredAddress(formattedAddress);
                    break;
                case 'bci_ra':
                    setFormattedBillingAddress(formattedAddress);
                    break;
                case 'sci_ra':
                    setFormattedServiceAddress(formattedAddress);
                    break;
                default:
                    break;
            }
        };
    };

    const { merchantApplication } = useAddMerchantContext();
    const isEditingDisabled = isApplicationEditable(merchantApplication);

    return (
        <Formik
            enableReinitialize={false}
            onSubmit={(values, helpers) => {
                if (vatRegistered && !values.pi_vatNumber) {
                    helpers.setFieldError('pi_vatNumber', 'VAT Number is required');
                } else if (!vatRegistered) {
                    helpers.setFieldError('pi_vatNumber', null);
                }

                const rawInput = {
                    ...values,
                    directors: [values.directors],
                    companyIncorporationDate:
                        values?.companyIncorporationDate?.toISOString() || null,
                    pi_accountManager: Number(values?.pi_accountManager),
                    pi_creditLimit: values?.pi_creditLimit || 0,
                    pi_paymentTerms: values?.pi_paymentTerms || '12',
                } as AddPartnerInput;

                const {
                    countryCode,
                    registeredAddressSearch,
                    tradingAddressSearch,
                    registeredAddress,
                    ...cleanInput
                } = rawInput;

                const { partnerId, partnerType } = application;
                addPartner(
                    {
                        ...cleanInput,
                        step: 2,
                        partnerType,
                        partnerId,
                    },
                    activeStep
                );
            }}
            initialValues={initialValues}
            validationSchema={step2ValidationSchema(postalLabel)}
        >
            {(formik) => {
                return (
                    <>
                        {(validatingVatNumber || searchingForAddress) && <Loader />}
                        <Box display="flex" flexDirection="column" gap="24px">
                            <Card heading="Partner Information">
                                <DropDown
                                    name="pi_BusinessType"
                                    label="Business Type"
                                    placeholder="Select Business Type"
                                    options={Object.values(BUSINESS_TYPES).map((value) => {
                                        return {
                                            label: value.replace(/_/g, ' '),
                                            value,
                                        };
                                    })}
                                    boxStyling={{ maxWidth: '49%', marginBottom: '24px' }}
                                    onChange={(value: string) => {
                                        return setBusinessSelectedType(value);
                                    }}
                                />
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    marginBottom="24px"
                                    ref={grid1Ref}
                                >
                                    <SearchInput
                                        name="pi_legalCompanyName"
                                        label="Legal Company Name"
                                        value={formik.values.pi_legalCompanyName}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                        }}
                                        error={formik.errors.pi_legalCompanyName}
                                        boxStyling={{ maxWidth: '50%' }}
                                        onSearch={async (value) => {
                                            await companyLookUp({
                                                variables: { companyName: value.toLowerCase() },
                                            });
                                            setIsCompaniesOpen(true);
                                        }}
                                    />
                                    <CustomInput
                                        name="pi_website"
                                        label="Website"
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                        }}
                                        value={formik.values.pi_website}
                                        error={formik.errors.pi_website}
                                    />
                                </Box>
                                {!isSoleTrader && (
                                    <SearchInput
                                        name="pi_registeredNumber"
                                        label="Registered Number"
                                        value={formik.values.pi_registeredNumber}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                        }}
                                        error={formik.errors.pi_registeredNumber}
                                        boxStyling={{ maxWidth: '49%' }}
                                        onSearch={async (value) => {
                                            await companyLookUp({
                                                variables: { companyNumber: value.toString() },
                                            });
                                            setIsCompaniesOpen(true);
                                        }}
                                    />
                                )}
                                <Box
                                    height="2px"
                                    width="98%"
                                    margin="24px auto"
                                    bgcolor="#DADADA"
                                />

                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="flex-end"
                                    gap="10px"
                                    ref={grid2Ref}
                                >
                                    <FormLabel id="vatRegistered-radio" sx={{ color: '#575757' }}>
                                        Vat Registered
                                    </FormLabel>
                                    <FormControl>
                                        <RadioGroup
                                            aria-labelledby="vat-registered-radio-buttons"
                                            row
                                            name="pi_vatRegistered"
                                            defaultValue={formik.values.pi_vatRegistered}
                                            onChange={(event) => {
                                                if (isEditingDisabled) {
                                                    return;
                                                }
                                                const value = JSON.parse(event.target.value);

                                                formik.setFieldValue('pi_vatRegistered', value);
                                                value === true
                                                    ? setIsVatRegistered(true)
                                                    : setIsVatRegistered(false);
                                            }}
                                        >
                                            <FormControlLabel
                                                value
                                                control={<Radio disabled={isEditingDisabled} />}
                                                label="Yes"
                                                disabled={isEditingDisabled}
                                            />
                                            <FormControlLabel
                                                value={false}
                                                control={<Radio disabled={isEditingDisabled} />}
                                                label="No"
                                                disabled={isEditingDisabled}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </Box>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    marginBottom="24px"
                                    ref={grid3Ref}
                                >
                                    <CustomInput
                                        name="pi_tradingName"
                                        label="Trading Name"
                                        value={formik.values.pi_tradingName}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                        }}
                                        error={formik.errors.pi_tradingName}
                                        boxStyling={{ maxWidth: '50%' }}
                                    />
                                    {vatRegistered && (
                                        <CustomInput
                                            name="pi_vatNumber"
                                            label="VAT Number"
                                            subLabel="(Please include the FULL VAT Number inc the country code)"
                                            value={formik.values.pi_vatNumber}
                                            onChange={formik.handleChange}
                                            error={formik.errors.pi_vatNumber}
                                            boxStyling={{ maxWidth: '50%' }}
                                        />
                                    )}
                                </Box>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    marginBottom="24px"
                                    ref={grid4Ref}
                                >
                                    <DropDown
                                        name="pi_accountManager"
                                        label="Account Manager"
                                        placeholder="Select an account manager"
                                        options={accountManagerOptions}
                                        boxStyling={{ maxWidth: '50%' }}
                                    />
                                    <CustomCurrencyInput
                                        name="pi_creditLimit"
                                        amount={formik.values.pi_creditLimit}
                                        currencySymbol={formik.values.currency}
                                        placeholder="Enter your credit limit"
                                        onNumberChange={formik.setFieldValue}
                                        label="Credit Limit"
                                        error={formik.errors.pi_creditLimit}
                                        boxStyling={{ maxWidth: '50%', flex: 0.49 }}
                                    />
                                </Box>
                                <CustomInput
                                    name="pi_paymentTerms"
                                    label="Payment Terms (days)"
                                    value={formik.values.pi_paymentTerms}
                                    onChange={(e) => {
                                        formik.handleChange(e);
                                    }}
                                    error={formik.errors.pi_paymentTerms}
                                    boxStyling={{ maxWidth: '50%' }}
                                />
                            </Card>
                            <Box ref={grid5Ref}>
                                <Step2AddressInformation
                                    formik={formik}
                                    onFindAddress={onFindAddress}
                                    formattedAddressInformation={formattedAddressInformation}
                                    formattedTradingAddress={formattedTradingAddress}
                                    postalLabel={postalLabel}
                                />
                            </Box>
                            <Box ref={grid6Ref}>
                                <Step2PrimaryContactInformation
                                    formik={formik}
                                    onUseForContact={onUseForContact}
                                    onFindAddress={onFindAddress}
                                    formattedAddress={formattedRegisteredAddress}
                                    postalLabel={postalLabel}
                                />
                            </Box>
                            <Box ref={grid7Ref}>
                                <ContactInformationComponent
                                    heading="Billing Contact Information"
                                    namePrefix="bci_"
                                    formik={formik}
                                    onFindAddress={onFindAddress}
                                    formattedAddress={formattedBillingAddress}
                                    postalLabel={postalLabel}
                                />
                            </Box>

                            <ContactInformationComponent
                                heading="Service Contact Information"
                                namePrefix="sci_"
                                formik={formik}
                                onFindAddress={onFindAddress}
                                formattedAddress={formattedServiceAddress}
                                postalLabel={postalLabel}
                            />
                            <Modal
                                open={isCompaniesOpen}
                                onClose={() => {
                                    return setIsCompaniesOpen(false);
                                }}
                            >
                                <CompanyLookUpList
                                    data={companies}
                                    onClose={() => {
                                        return setIsCompaniesOpen(false);
                                    }}
                                    onSelectCompanyReport={(
                                        companyReport: CompanyReportResponse
                                    ) => {
                                        const { addressLine1, town, county } = extractAddress(
                                            companyReport?.companyAddress
                                        );
                                        formik.setValues((prev) => {
                                            return {
                                                ...prev,
                                                pi_legalCompanyName: companyReport?.companyName,
                                                pi_registeredNumber:
                                                    companyReport?.registrationNumber,
                                                pi_website: companyReport?.websiteURL,
                                                ai_ra_addressLine1: addressLine1,
                                                ai_ra_city: town,
                                                ai_ra_county: county,
                                                ai_ra_eircode: companyReport?.eircode,
                                            };
                                        });
                                        setIsCompaniesOpen(false);
                                    }}
                                />
                            </Modal>

                            <AddressListModal
                                isOpen={openAddressModal.open}
                                onClose={() => {
                                    return setOpenAddressModal({ open: false, formValue: '' });
                                }}
                                addressList={addresses}
                                onSelectedAddress={(
                                    selectedAddress: Address,
                                    formattedAddress: string
                                ) => {
                                    onRetrievalOfAddress(selectedAddress, formattedAddress)(formik);
                                }}
                            />

                            <NavigationButtons
                                onBack={handleBack}
                                onNext={async () => {
                                    try {
                                        await step2ValidationSchema(postalLabel).validate(
                                            formik.values
                                        );
                                        if (formik.values.pi_vatRegistered) {
                                            return await onValidateVAT(
                                                formik?.values?.pi_vatNumber,
                                                () => {
                                                    formik.setFieldError(
                                                        'pi_vatNumber',
                                                        'Enter a valid VAT number'
                                                    );
                                                    grid2Ref.current.scrollIntoView({
                                                        behavior: 'smooth',
                                                        block: 'start',
                                                    });
                                                },
                                                () => {
                                                    formik.setFieldError('pi_vatNumber', null);
                                                    formik.handleSubmit();
                                                }
                                            );
                                        }
                                        return formik.handleSubmit();
                                    } catch (error) {
                                        if (
                                            formik.errors.pi_legalCompanyName ||
                                            formik.errors.pi_legalCompanyName ||
                                            formik.errors.pi_registeredNumber
                                        ) {
                                            grid1Ref.current.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'start',
                                            });
                                        } else if (
                                            formik.errors.ai_ta_eircode ||
                                            formik.errors.ai_ta_addressLine1 ||
                                            formik.errors.ai_ta_addressLine2 ||
                                            formik.errors.ai_ta_city ||
                                            formik.errors.ai_ta_county ||
                                            formik.errors.ai_ta_country
                                        ) {
                                            grid5Ref.current.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'start',
                                            });
                                        } else if (
                                            formik.values.pci_email ||
                                            formik.errors.pci_firstName ||
                                            formik.errors.pci_lastName ||
                                            formik.errors.pci_mobilePhoneNumber ||
                                            formik.errors.pci_position ||
                                            formik.errors.pci_ra_addressLine1 ||
                                            formik.errors.pci_ra_addressLine2 ||
                                            formik.errors.pci_ra_city ||
                                            formik.errors.pci_ra_country ||
                                            formik.errors.pci_ra_county ||
                                            formik.errors.pci_ra_eircode ||
                                            formik.errors.pci_telephoneNumber
                                        ) {
                                            grid6Ref.current.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'start',
                                            });
                                        } else if (
                                            formik.errors.bci_email ||
                                            formik.errors.bci_firstName ||
                                            formik.errors.bci_position ||
                                            formik.errors.bci_mobilePhoneNumber ||
                                            formik.errors.bci_lastName ||
                                            formik.errors.bci_ra_eircode ||
                                            formik.errors.bci_ra_city ||
                                            formik.errors.bci_ra_addressLine1
                                        ) {
                                            grid7Ref.current.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'start',
                                            });
                                        }

                                        showErrorToast('Please fill in required fields');
                                    }
                                }}
                            />
                        </Box>
                    </>
                );
            }}
        </Formik>
    );
};
